import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ArchiveCard, { ArchiveItem } from "@/components/archive/ArchiveCard";
import { Input } from "@/components/ui/input";
import { Search, Library } from "lucide-react";
import { useCuratedPosts } from "@/hooks/useCuratedPosts";

const CATEGORIES = ["All", "Policy", "Community", "Technology", "Environment", "Education", "Housing", "Health", "Economic"];

type SourceFilter =
  | "all"
  | "community"
  | "curated"
  | "case_study"
  | "article"
  | "legislation"
  | "grant"
  | "partner";
type TypeFilter = "all" | "concept" | "project";
type SortFilter = "newest" | "top";

const SOURCE_LABELS: Record<SourceFilter, string> = {
  all: "All",
  community: "Community ideas",
  curated: "EM Curated",
  case_study: "Case studies",
  article: "Insights",
  legislation: "Legislation",
  grant: "Grants",
  partner: "Partners",
};

const Archive = () => {
  const [source, setSource] = useState<SourceFilter>("all");
  const [typeF, setTypeF] = useState<TypeFilter>("all");
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState<SortFilter>("newest");
  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = "Archive | Alabama community knowledge | emrkt.co";
  }, []);

  const { data: ideas = [] } = useQuery({
    queryKey: ["archive-ideas"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ideas")
        .select("id,title,description,image_url,category,idea_type,vote_count,created_at,status")
        .eq("status", "published")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: curated = [] } = useCuratedPosts();

  const { data: caseStudies = [] } = useQuery({
    queryKey: ["archive-case-studies"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_case_studies");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: articles = [] } = useQuery({
    queryKey: ["archive-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("id,slug,title,excerpt,image_url,category,author,published_at,created_at")
        .eq("status", "published")
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: legislation = [] } = useQuery({
    queryKey: ["archive-legislation"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legislation")
        .select("id,bill_id,title,description,status,area,county,date,source_url,created_at")
        .order("date", { ascending: false, nullsFirst: false })
        .limit(150);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: grants = [] } = useQuery({
    queryKey: ["archive-grants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("grant_programs")
        .select("id,title,description,focus_areas,next_deadline,program_url,source_url,updated_at,created_at,verified")
        .order("updated_at", { ascending: false })
        .limit(150);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: partners = [] } = useQuery({
    queryKey: ["archive-partners"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates")
        .select("id,slug,name,tagline,blurb,logo_url,category,counties,tier,status,is_published,created_at")
        .eq("status", "active")
        .eq("is_published", true)
        .order("created_at", { ascending: false })
        .limit(150);
      if (error) throw error;
      return data ?? [];
    },
  });

  const items = useMemo<ArchiveItem[]>(() => {
    const ideaItems: ArchiveItem[] = ideas.map((i: any) => ({
      kind: "idea",
      id: i.id,
      title: i.title,
      summary: i.description,
      cover: i.image_url,
      category: i.category,
      type: i.idea_type ?? "concept",
      date: i.created_at,
      vote_count: i.vote_count ?? 0,
      href: `/ideas/${i.id}`,
    }));
    const curItems: ArchiveItem[] = curated.map((c) => ({
      kind: "curated",
      id: c.id,
      title: c.title,
      summary: c.summary,
      cover: c.cover_image_url,
      category: c.category,
      source_org: c.source_org_name,
      source_url: c.source_url,
      date: c.published_at ?? c.created_at,
      href: `/archive/curated/${c.slug}`,
    }));
    const caseItems: ArchiveItem[] = (caseStudies as any[]).map((c) => ({
      kind: "case_study",
      id: c.project_id,
      title: c.idea_title,
      summary: c.latest_summary ?? c.idea_description,
      cover: c.idea_image_url,
      category: c.idea_category,
      outcome: c.latest_outcome,
      lessons_count: Number(c.public_lessons_count ?? 0),
      date: c.latest_lesson_at,
      href: `/archive/case-studies/${c.project_id}`,
    }));
    const articleItems: ArchiveItem[] = (articles as any[]).map((a) => ({
      kind: "article",
      id: a.id,
      title: a.title,
      summary: a.excerpt,
      cover: a.image_url,
      category: a.category,
      author: a.author,
      date: a.published_at ?? a.created_at,
      href: `/insights/${a.slug}`,
    }));
    const legItems: ArchiveItem[] = (legislation as any[]).map((l) => ({
      kind: "legislation",
      id: l.id,
      title: l.title,
      summary: l.description,
      cover: null,
      category: l.area,
      bill_id: l.bill_id,
      status: l.status,
      county: l.county,
      date: l.date ?? l.created_at,
      href: "/legislation",
      external_url: l.source_url,
    }));
    const grantItems: ArchiveItem[] = (grants as any[]).map((g) => ({
      kind: "grant",
      id: g.id,
      title: g.title,
      summary: g.description,
      cover: null,
      category: Array.isArray(g.focus_areas) ? g.focus_areas[0] ?? null : null,
      next_deadline: g.next_deadline,
      date: g.updated_at ?? g.created_at,
      href: "/opportunities",
      external_url: g.program_url ?? g.source_url,
    }));
    const partnerItems: ArchiveItem[] = (partners as any[]).map((p) => ({
      kind: "partner",
      id: p.id,
      title: p.name,
      summary: p.tagline ?? p.blurb,
      cover: p.logo_url,
      category: p.category,
      counties: p.counties,
      tier: p.tier,
      date: p.created_at,
      href: `/affiliates/${p.slug}`,
    }));

    let merged = [
      ...ideaItems,
      ...curItems,
      ...caseItems,
      ...articleItems,
      ...legItems,
      ...grantItems,
      ...partnerItems,
    ];

    if (source === "community") merged = merged.filter((m) => m.kind === "idea");
    else if (source === "curated") merged = merged.filter((m) => m.kind === "curated");
    else if (source === "case_study") merged = merged.filter((m) => m.kind === "case_study");
    else if (source === "article") merged = merged.filter((m) => m.kind === "article");
    else if (source === "legislation") merged = merged.filter((m) => m.kind === "legislation");
    else if (source === "grant") merged = merged.filter((m) => m.kind === "grant");
    else if (source === "partner") merged = merged.filter((m) => m.kind === "partner");

    if (typeF !== "all")
      merged = merged.filter((m) => m.kind === "idea" && m.type === typeF);
    if (category !== "All")
      merged = merged.filter((m) => (m.category ?? "").toLowerCase() === category.toLowerCase());
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      merged = merged.filter((m) => {
        const extras: string[] = [];
        if (m.kind === "legislation") {
          if (m.bill_id) extras.push(m.bill_id);
          if (m.county) extras.push(m.county);
        }
        if (m.kind === "partner") extras.push(...(m.counties ?? []));
        if (m.kind === "article" && m.author) extras.push(m.author);
        if (m.kind === "curated") extras.push(m.source_org);
        const hay = [m.title, m.summary ?? "", m.category ?? "", ...extras]
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      });
    }
    if (sort === "top") {
      merged.sort((a, b) => {
        const av = a.kind === "idea" ? a.vote_count : 0;
        const bv = b.kind === "idea" ? b.vote_count : 0;
        return bv - av;
      });
    } else {
      merged.sort((a, b) => +new Date(b.date) - +new Date(a.date));
    }
    return merged;
  }, [ideas, curated, caseStudies, articles, legislation, grants, partners, source, typeF, category, sort, q]);

  const counts = useMemo(() => {
    return {
      all: items.length, // after current filters except source — recalc separately below
    };
  }, [items]);
  // unsourced totals for the chip badges (always show counts before source filter)
  const totals = useMemo(() => ({
    community: ideas.length,
    curated: curated.length,
    case_study: (caseStudies as any[]).length,
    article: (articles as any[]).length,
    legislation: (legislation as any[]).length,
    grant: (grants as any[]).length,
    partner: (partners as any[]).length,
  }), [ideas, curated, caseStudies, articles, legislation, grants, partners]);
  const totalAll =
    totals.community + totals.curated + totals.case_study + totals.article +
    totals.legislation + totals.grant + totals.partner;
  void counts;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <Library className="h-8 w-8 text-primary" />
              <h1 className="font-display text-3xl font-bold">Archive</h1>
            </div>
            <p className="text-muted-foreground mt-1 max-w-2xl">
              The searchable knowledge library for Alabama: community ideas, case studies, insights,
              legislation, grants, and partner organizations — one feed, one search.
            </p>
          </div>

          {/* Filter bar */}
          <div className="sticky top-16 z-30 bg-background/85 backdrop-blur-xl border-b border-border py-3 mb-6 -mx-4 sm:-mx-6 px-4 sm:px-6 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {(Object.keys(SOURCE_LABELS) as SourceFilter[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setSource(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                    source === s
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  }`}
                >
                  {SOURCE_LABELS[s]}
                  <span className="ml-1.5 opacity-60">
                    {s === "all" ? totalAll : (totals as any)[s] ?? 0}
                  </span>
                </button>
              ))}
              <span className="w-px h-5 bg-border mx-1" />
              {(source === "all" || source === "community") &&
                (["all", "concept", "project"] as TypeFilter[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTypeF(t)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                      typeF === t
                        ? "bg-primary/15 text-primary border border-primary/30"
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-transparent"
                    }`}
                  >
                    {t === "all" ? "All types" : `${t}s`}
                  </button>
                ))}
              <span className="w-px h-5 bg-border mx-1" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortFilter)}
                className="bg-secondary border border-border rounded-full px-3 py-1.5 text-xs"
              >
                <option value="newest">Newest</option>
                <option value="top">Most upvoted</option>
              </select>
              <div className="relative ml-auto w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search ideas, case studies, bills, grants, partners…"
                  className="pl-8 h-8 rounded-full text-xs"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                    category === c
                      ? "bg-foreground text-background"
                      : "bg-secondary/60 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <p className="text-lg mb-1">Nothing matches those filters.</p>
              <p className="text-sm">Try a different source or category.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-3">
                {items.length} result{items.length === 1 ? "" : "s"}
              </p>
              <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {items.map((it) => (
                <ArchiveCard key={`${it.kind}-${it.id}`} item={it} />
              ))}
              </div>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Archive;